import { forwardRef, useCallback, useImperativeHandle, useState } from 'react'

import { ITextInputProps } from './ITextInputProps'
import { TextInput } from './TextInput'
import {
  creditCardMask,
  currencyMask,
  cvvMask,
  dateMask,
  documentNumberMask,
  floatMask,
  integerMask,
  monthYearMask,
  phoneMask,
  zipCodeMask,
} from './utils/mask'

interface Props extends ITextInputProps {
  mask?:
    | 'float'
    | 'integer'
    | 'money'
    | 'zipCode'
    | 'creditCard'
    | 'numberString'
    | 'monthYear'
    | 'phone'
    | 'cvv'
    | 'cpf'
    | 'date'
  onChange: (value: any) => void
}

export interface IMaskedInput {
  setValue: (value: any) => void
}

const Component: React.ForwardRefRenderFunction<IMaskedInput, Props> = (
  { value, onChange, mask, ...props },
  ref,
) => {
  const [inputData, setInputData] = useState<string>(() => {
    switch (mask) {
      case 'integer':
        return integerMask(String(value ?? 0))
      case 'float':
        if (typeof value === 'number') {
          return floatMask(value.toFixed(2))
        }
        return floatMask(String(value ?? 0))
      case 'money':
        return currencyMask(String(value ?? 0))
      case 'phone':
        return phoneMask(String(value ?? ''))
      case 'monthYear':
        return monthYearMask(String(value ?? ''))
      case 'date':
        return dateMask(String(value ?? ''))
      case 'cpf':
        return documentNumberMask(String(value ?? ''))
      case 'cvv':
        return cvvMask(String(value ?? ''))
      default:
        return ''
    }
  })

  const handleChange = useCallback(
    (v: string) => {
      let input = String(v)

      switch (mask) {
        case 'creditCard':
          input = creditCardMask(input)
          break
        case 'numberString':
          input = integerMask(input)
          break
        case 'integer':
          input = integerMask(input || '0')
          break
        case 'money':
          input = currencyMask(input)
          break
        case 'zipCode':
          input = zipCodeMask(input)
          break
        case 'phone':
          input = phoneMask(input)
          break
        case 'cpf':
          input = documentNumberMask(input)
          break
        case 'monthYear':
          input = monthYearMask(input)
          break
        case 'date':
          input = dateMask(input)
          break
        case 'cvv':
          input = cvvMask(input)
          break
        case 'float':
        default:
          input = floatMask(input || '0')
      }

      setInputData(input)

      if (mask === 'float') {
        onChange && onChange(parseFloat(input.replace(',', '.')))
      } else if (mask === 'integer') {
        onChange && onChange(parseInt(input))
      } else if (mask === 'money') {
        onChange && onChange(parseFloat(floatMask(input).replace(',', '.')))
      } else {
        onChange && onChange(input)
      }
    },
    [onChange, mask],
  )

  useImperativeHandle(ref, () => ({
    setValue(value) {
      handleChange(value)
    },
  }))

  return (
    <TextInput
      {...props}
      value={inputData}
      onChange={(e) => handleChange(e.target.value)}
    />
  )
}

export const MaskedInput = forwardRef(Component)
